/**
 * What is *there*, as opposed to what is dangerous. Deliberately separate from
 * the corridor score: a hospital near the route is not a threat, and folding it
 * into a risk number would make that number mean nothing.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from '@mui/material';
import {
  AssetCategory,
  DistanceUnits,
  RouteAsset,
  RouteAssetsReport,
  ServiceCategory,
} from '../types';
import { formatDistance } from '../geo/format';
import { IntelSection } from './intel';
import {
  DockAction,
  EmptyState,
  ErrorLine,
  GOLD,
  Loading,
  Provenance,
  TabHeader,
  WARN,
} from './DockPrimitives';
import { DockTabHelp } from '../../missions/DockTabHelp';

/** Ordered by how much each one changes a plan, not alphabetically. */
const SERVICE_ORDER: ServiceCategory[] = ['hospital', 'fuel', 'police', 'fire', 'clinic', 'pharmacy'];

const PLURAL: Record<AssetCategory, string> = {
  hospital: 'hospitals',
  clinic: 'clinics',
  pharmacy: 'pharmacies',
  police: 'police stations',
  fire: 'fire stations',
  fuel: 'fuel',
  bridge: 'bridges',
  tunnel: 'tunnels',
  ford: 'fords',
  level_crossing: 'level crossings',
  gate: 'gates',
};

const AssetRow: React.FC<{
  asset: RouteAsset;
  units: DistanceUnits;
  onFocus?: () => void;
}> = ({ asset, units, onFocus }) => (
  <Box
    onClick={onFocus}
    role={onFocus ? 'button' : undefined}
    tabIndex={onFocus ? 0 : -1}
    onKeyDown={(e) => { if (e.key === 'Enter') onFocus?.(); }}
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 0.75,
      py: 0.3,
      cursor: onFocus ? 'pointer' : 'default',
      '&:hover .asset-name': onFocus ? { textDecoration: 'underline' } : undefined,
      '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: '0.64rem', width: 46, flexShrink: 0, textAlign: 'right' }}
    >
      {formatDistance(asset.distance_along_m, units)}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography className="asset-name" variant="caption" noWrap sx={{ display: 'block', fontWeight: 600 }}>
        {asset.name}
      </Typography>
      {asset.detail && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: 'text.secondary' }}>
          {asset.detail}
        </Typography>
      )}
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem', flexShrink: 0 }}>
      {asset.offset_m < 30 ? 'on route' : `${formatDistance(asset.offset_m, units)} off`}
    </Typography>
  </Box>
);

export interface PlacesTabProps {
  section: IntelSection<RouteAssetsReport>;
  units: DistanceUnits;
  hasRoute: boolean;
  radiusM?: number;
  onRadiusChange?: (metres: number) => void;
  onFocusAsset?: (asset: RouteAsset) => void;
}

const PlacesTab: React.FC<PlacesTabProps> = ({
  section,
  units,
  hasRoute,
  radiusM,
  onRadiusChange,
  onFocusAsset,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [expanded, setExpanded] = useState<ServiceCategory | null>(null);
  const [crossingsOpen, setCrossingsOpen] = useState(false);

  const report = section.data;

  const byCategory = useMemo(() => {
    const groups = new Map<AssetCategory, RouteAsset[]>();
    for (const asset of report?.services || []) {
      const list = groups.get(asset.category) || [];
      list.push(asset);
      groups.set(asset.category, list);
    }
    return groups;
  }, [report]);

  const scan = () => {
    if (section.enabled) section.refresh?.();
    else section.setEnabled?.(true);
  };
  const canScan = hasRoute && (!!section.setEnabled || !!section.refresh);
  const radius = radiusM ?? report?.service_radius_m ?? 250;
  const radiusText = radius >= 1000 ? `${(radius / 1000).toFixed(1)} km` : `${radius} m`;

  return (
    <Box>
      <TabHeader
        title="Places"
        hint={report ? radiusText : undefined}
        help={<DockTabHelp tab="along" />}
        actions={(
          <>
            {onRadiusChange && (
              <DockAction onClick={() => setSettingsOpen(true)} title="How far off-route to look">
                Radius
              </DockAction>
            )}
            {canScan && (
              <DockAction onClick={scan} disabled={section.loading}>
                {report ? 'Rescan' : 'Scan'}
              </DockAction>
            )}
          </>
        )}
      />

      {section.loading && <Loading label="Reading OpenStreetMap…" />}
      {section.error && <ErrorLine>{section.error}</ErrorLine>}

      {!report && !section.loading && (
        <EmptyState>
          {hasRoute
            ? 'Hospitals, fuel, police and crossings on and beside this route, from OpenStreetMap. Results stay here once scanned.'
            : 'Plan a route to look up the infrastructure along it.'}
        </EmptyState>
      )}

      {report && !report.dataset.available && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.66rem' }}>
          {report.dataset.note || 'OpenStreetMap is unavailable right now.'}
        </Typography>
      )}

      {report && report.dataset.available && (
        <>
          {report.dataset.matched === 0 && (
            <EmptyState>Nothing mapped within {radiusText} of this route.</EmptyState>
          )}

          {report.gaps.map((gap) => (
            <Typography
              key={`${gap.from_m}-${gap.to_m}`}
              variant="caption"
              sx={{ display: 'block', color: WARN, fontWeight: 600 }}
            >
              No fuel between {formatDistance(gap.from_m, units)} and {formatDistance(gap.to_m, units)} in
            </Typography>
          ))}

          {report.constrictions.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Box
                onClick={() => setCrossingsOpen(!crossingsOpen)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setCrossingsOpen(!crossingsOpen); }}
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  cursor: 'pointer',
                  py: 0.25,
                  '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
                  {report.constrictions.length} crossing
                  {report.constrictions.length === 1 ? '' : 's'} on the route
                </Typography>
                <Typography variant="caption" sx={{ color: GOLD, fontSize: '0.64rem' }}>
                  {crossingsOpen ? 'Hide' : 'Show'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                Bridges, tunnels and gates are where alternatives cannot exist.
              </Typography>
              {crossingsOpen && report.constrictions.map((asset) => (
                <AssetRow
                  key={asset.id}
                  asset={asset}
                  units={units}
                  onFocus={onFocusAsset ? () => onFocusAsset(asset) : undefined}
                />
              ))}
            </Box>
          )}

          {SERVICE_ORDER.map((category) => {
            const assets = byCategory.get(category);
            if (!assets || assets.length === 0) return null;
            const open = expanded === category;
            const nearest = assets.reduce((a, b) => (a.offset_m <= b.offset_m ? a : b));
            return (
              <Box key={category} sx={{ mt: 0.4 }}>
                <Box
                  onClick={() => setExpanded(open ? null : category)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setExpanded(open ? null : category); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.75,
                    cursor: 'pointer',
                    py: 0.25,
                    '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {assets.length} {PLURAL[category]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    nearest {formatDistance(nearest.offset_m, units)} off
                  </Typography>
                  <Typography variant="caption" sx={{ color: GOLD, fontSize: '0.64rem' }}>
                    {open ? 'Hide' : 'Show'}
                  </Typography>
                </Box>
                {open && assets.map((asset) => (
                  <AssetRow
                    key={asset.id}
                    asset={asset}
                    units={units}
                    onFocus={onFocusAsset ? () => onFocusAsset(asset) : undefined}
                  />
                ))}
              </Box>
            );
          })}

          {report.dataset.truncated && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.5, fontSize: '0.64rem', color: 'text.disabled' }}
            >
              Result limit reached — narrow the search radius for a complete list.
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem', color: 'text.disabled' }}
          >
            OpenStreetMap contributors{report.cached ? ' · cached' : ''}
          </Typography>
        </>
      )}

      <Provenance section={section} onRefresh={canScan ? scan : undefined} />

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Places along the route</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1.5 }}>
            Search radius — {radiusText} either side
          </Typography>
          <Slider
            size="small"
            value={radius}
            min={250}
            max={5000}
            step={250}
            marks={[{ value: 250, label: '250 m' }, { value: 5000, label: '5 km' }]}
            valueLabelDisplay="auto"
            onChange={(_, value) => onRadiusChange?.(value as number)}
            sx={{ color: GOLD, mb: 1.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Crossings are always matched to the route itself rather than this radius —
            a bridge on a parallel road is not a bridge you drive over.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Data comes from OpenStreetMap, which is current but community-maintained.
            Treat a missing feature as unknown rather than absent.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PlacesTab;
